import React, { useCallback, useEffect, useRef } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table as TableUi,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Spinner } from '@/components/spinner';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading: boolean;
  meta?: Record<string, unknown>;
  onRowClick?: (
    row: TData,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => void;
  rowClassName?: (row: TData) => string;
}

export const Table = <TData, TValue>({
  columns,
  data,
  loading,
  meta,
  onRowClick,
  rowClassName,
}: DataTableProps<TData, TValue>) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta,
  });

  const updateScrollState = useCallback(() => {
    const el = tableRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    if (!hasOverflow) {
      el.dataset.scroll = 'none';
      return;
    }
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    el.dataset.scroll = atEnd ? 'end' : 'scrollable';
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = tableRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState, data.length, columns.length, loading]);

  return (
    <div className={'rounded-md border'}>
      <TableUi tableRef={tableRef}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className={
                      (header.column.columnDef.meta as { className?: string })
                        ?.className
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length && !loading ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                onClick={(e) => onRowClick?.(row.original, e)}
                className={
                  rowClassName
                    ? rowClassName(row.original)
                    : onRowClick
                      ? 'cursor-pointer'
                      : ''
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={
                      (cell.column.columnDef.meta as { className?: string })
                        ?.className
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : loading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-24'>
                <div className='flex justify-center items-center'>
                  <Spinner />
                </div>
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-24 text-center'>
                <div className='flex justify-center items-center'>
                  No results.
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </TableUi>
    </div>
  );
};
